import type { Edge, EdgeChange, NodeChange } from '@xyflow/react';
import { useContext } from 'react';
import React from 'react';

import type {
  AttackStage,
  CustomNode,
  EngagementMetadata,
  HostGroup,
} from '@/sections/attacks/attackPaths/types';

/** Which view is active in the attack path page */
export type AttackPathView = 'graph' | 'narrative';

/**
 * Context type for attack path state and operations.
 */
export interface AttackContextType {
  attackPaths: string[];

  name: string;
  nodes: CustomNode[];
  edges: Edge[];
  stages: AttackStage[];
  hosts: HostGroup[];
  engagement: EngagementMetadata;
  isEditMode: boolean;
  selectedAttackPath: string;
  activeView: AttackPathView;
  focusedStageIndex: number | null;
  /** When true, asset nodes hide their action cards (used in mini-graphs) */
  compactMode: boolean;

  setName: React.Dispatch<React.SetStateAction<string>>;
  setNodes: React.Dispatch<React.SetStateAction<CustomNode[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setStages: React.Dispatch<React.SetStateAction<AttackStage[]>>;
  setHosts: React.Dispatch<React.SetStateAction<HostGroup[]>>;
  setEngagement: React.Dispatch<React.SetStateAction<EngagementMetadata>>;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedAttackPath: (id: string) => void;
  setActiveView: React.Dispatch<React.SetStateAction<AttackPathView>>;
  setFocusedStageIndex: React.Dispatch<React.SetStateAction<number | null>>;

  addNode: (node: CustomNode) => void;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, data: Partial<CustomNode['data']>) => void;
  addEdge: (edge: Edge) => void;
  removeEdge: (edgeId: string) => void;
  onNodesChange: (changes: NodeChange<CustomNode>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;

  isFetchingAttackPaths: boolean;
  isFetchingAttackPath: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  onCreate: () => void;
  onChange: (path: string) => void;
  recenter: (delay?: number) => void;
}

export const AttackContext = React.createContext<AttackContextType | undefined>(
  undefined
);

/**
 * Hook to access attack path context.
 * Must be used within an AttackProvider.
 */
export const useAttackState = () => {
  const context = useContext(AttackContext);
  if (context === undefined) {
    throw new Error('useAttackState must be used within an AttackProvider');
  }
  return context;
};
